import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * PenetrationHeatmap - Agency × Product penetration matrix
 *
 * Shows spend intensity as colored cells with optional dollar values.
 * Presentational component - data via props, theme via CSS variables.
 */
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChartDrillList } from './ChartDrillList.js';
/** Format currency for display */
const formatCurrency = (value) => {
    if (value >= 1000000)
        return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000)
        return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
};
/** Calculate intensity (0-1) based on color mode */
const calculateIntensity = (spend, maxSpend, mode) => {
    if (spend <= 0 || maxSpend <= 0)
        return 0;
    switch (mode) {
        case 'log':
            // Log scale compresses dynamic range
            return Math.log10(spend + 1) / Math.log10(maxSpend + 1);
        case 'linear':
            return spend / maxSpend;
        case 'rank':
            // Rank mode would require all values - simplified to linear here
            return spend / maxSpend;
        default:
            return spend / maxSpend;
    }
};
export function PenetrationHeatmap({ rows, columns, cells, maxHeight = 500, colorMode = 'log', showValues = true, onCellClick, onEmptyCellClick, }) {
    // Build cell lookup map
    const cellMap = useMemo(() => {
        const map = new Map();
        cells.forEach((cell) => {
            map.set(`${cell.rowId}::${cell.colId}`, cell);
        });
        return map;
    }, [cells]);
    // Calculate max spend for intensity scaling
    const maxSpend = useMemo(() => {
        return Math.max(...cells.map((c) => c.spend), 1);
    }, [cells]);
    // Group columns by category
    const columnGroups = useMemo(() => {
        const groups = [];
        let currentGroup = '';
        columns.forEach((col) => {
            const group = col.group || '';
            if (group !== currentGroup) {
                groups.push({ name: group, cols: [col] });
                currentGroup = group;
            }
            else if (groups.length > 0) {
                groups[groups.length - 1].cols.push(col);
            }
            else {
                groups.push({ name: '', cols: [col] });
            }
        });
        return groups;
    }, [columns]);
    /*
     * A cell's alpha. The hue is the sheet's (`--sch-curated`, indigo); the one
     * data-driven number is how saturated it is. An empty cell is a class, not
     * a colour: `.sch-cell--empty` draws the dashed outline.
     */
    const cellAlpha = (intensity) => Math.min(0.9, intensity * 0.75 + 0.15);
    /*
     * The column-header row pins under the group row. That offset was a
     * hard-coded 34px; the group row measures 30. Measured now, and the group
     * row only renders when it groups something (two or more named groups).
     */
    const groupRowRef = useRef(null);
    const [groupRowHeight, setGroupRowHeight] = useState(30);
    const measure = useCallback(() => {
        if (groupRowRef.current)
            setGroupRowHeight(Math.round(groupRowRef.current.getBoundingClientRect().height));
    }, []);
    useLayoutEffect(() => {
        measure();
        if (typeof ResizeObserver === 'undefined' || !groupRowRef.current)
            return undefined;
        const ro = new ResizeObserver(measure);
        ro.observe(groupRowRef.current);
        return () => ro.disconnect();
    }, [measure]);
    const hasColumnGroups = columnGroups.filter((g) => g.name).length > 1;
    const stickyTop = hasColumnGroups ? `${groupRowHeight}px` : '0px';
    if (rows.length === 0 || columns.length === 0) {
        return (_jsx("div", { className: "sch sch--pen sch-status", children: "No heatmap data available" }));
    }
    return (_jsxs("div", { className: "sch sch--pen sch-scroll", style: { '--sch-max-h': `${maxHeight}px` }, children: [_jsxs("table", { className: "sch-table", children: [hasColumnGroups && (_jsx("thead", { ref: groupRowRef, children: _jsxs("tr", { children: [_jsx("th", { className: "sch-corner" }), columnGroups.map((group, gi) => (_jsx("th", { colSpan: group.cols.length, className: "sch-group sch-group--named", children: group.name }, gi)))] }) })), _jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { className: "sch-row-head-th", style: { '--sch-top': stickyTop } }), columns.map((col) => (_jsx("th", { className: "sch-col", style: { '--sch-top': stickyTop }, title: col.label, children: col.label.length > 15 ? col.label.slice(0, 15) + '…' : col.label }, col.id)))] }) }), _jsx("tbody", { children: rows.map((row) => (_jsxs("tr", { children: [_jsxs("td", { className: "sch-row-head", children: [_jsx("div", { className: "sch-row-label", title: row.label, children: row.label }), row.sublabel && (_jsx("div", { className: "sch-row-sub", children: row.sublabel }))] }), columns.map((col) => {
                                    const cell = cellMap.get(`${row.id}::${col.id}`);
                                    const hasValue = cell && cell.spend > 0;
                                    const intensity = hasValue
                                        ? calculateIntensity(cell.spend, maxSpend, colorMode)
                                        : 0;
                                    const showText = showValues && hasValue && intensity > 0.3;
                                    return (_jsxs("td", { onClick: () => {
                                            if (hasValue && onCellClick) {
                                                onCellClick(cell, row, col);
                                            }
                                            else if (!hasValue && onEmptyCellClick) {
                                                onEmptyCellClick(row, col);
                                            }
                                        }, className: [
                                            'sch-cell',
                                            hasValue ? 'sch-cell--curated' : 'sch-cell--empty sch-cell--seed',
                                            onCellClick || onEmptyCellClick ? 'sch-cell--live' : '',
                                        ].filter(Boolean).join(' '), style: hasValue ? { '--sch-a': cellAlpha(intensity) } : undefined, title: hasValue
                                            ? `${row.label} × ${col.label}: ${cell.spendFormatted || formatCurrency(cell.spend)}`
                                            : `${row.label} × ${col.label}: No spend`, children: [showText && (_jsx("span", { className: `sch-val${intensity > 0.5 ? ' sch-val--light' : ''}`, children: cell.spendFormatted || formatCurrency(cell.spend) })), !hasValue && (_jsx("span", { className: "sch-dash", children: "+" }))] }, col.id));
                                })] }, row.id))) })] }), _jsxs("div", { className: "sch-legend", children: [_jsx("span", { children: "Less" }), _jsx("div", { className: "sch-gradient" }), _jsxs("span", { children: ["More \u00B7 max ", formatCurrency(maxSpend), " (", colorMode, " scale)"] })] }), _jsx(ChartDrillList, { label: "Vendor penetration \u2014 open transactions by cell", drills: onCellClick ? cells.filter((c) => c.spend > 0).map((c) => {
                    const row = rows.find((r) => r.id === c.rowId);
                    const col = columns.find((x) => x.id === c.colId);
                    if (!row || !col)
                        return null;
                    return {
                        key: `${c.rowId}-${c.colId}`,
                        label: `${row.label}, ${col.label}, ${c.spendFormatted || formatCurrency(c.spend)} — open transactions`,
                        onSelect: () => onCellClick(c, row, col),
                    };
                }).filter((d) => d !== null) : [] })] }));
}
export default PenetrationHeatmap;
